import React, { useEffect, useState } from "react";
import { BaseTable } from "@app/components/common/BaseTable/BaseTable";
import { BaseButton } from "@app/components/common/BaseButton/BaseButton";
import { BaseSpace } from "@app/components/common/BaseSpace/BaseSpace";
import { BasePopconfirm } from "@app/components/common/BasePopconfirm/BasePopconfirm";
import { Button, message, Modal, Transfer, Typography } from "antd";
import { adminApi, Role } from "@app/api/admin";
import { PlusOutlined, MinusOutlined, RedoOutlined } from "@ant-design/icons";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";

interface RoleManagementProps {
  userId?: number;
  employeeName?: string;
}

const RoleManagement: React.FC<RoleManagementProps> = ({
  userId,
  employeeName,
}) => {
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // 获取用户角色
  const fetchUserRoles = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const { data: roleList } = await adminApi.roles.list({
        page: 1,
        page_size: 100,
      });
      const { data } = await adminApi.roles.getUserRoles(userId);
      setAllRoles(roleList.list || []);

      setUserRoles(
        data.roles
          .map((role) => roleList.list?.find(item => item.name === role))
          .filter((role): role is Role => role !== undefined) || []
      );
    } catch (error: any) {
      console.error("error", error);
      // message.error(error.message || "获取用户角色失败");
    } finally {
      setLoading(false);
    }
  };



  useUpdateEffect(() => {
    if (userId) {
      fetchUserRoles();
    }
  }, [userId]);

  // 撤销角色
  // 修正撤销角色方法
  const handleRevokeRole = async (role: Role) => {
    if (!userId) return;
    try {
      // const role = userRoles.find((r) => r.id === roleId);
      // if (!role) {
      //   message.error("角色不存在");
      //   return;
      // }

      await adminApi.roles.revokeFromUser({
        user_id: userId,
        role: role.name,
      });
      message.success("撤销角色成功");
      fetchUserRoles();
    } catch (error: any) {
      message.error(error.message || "撤销角色失败");
    }
  };

  // 修正分配角色方法
  const handleAssignRoles = async () => {
    if (!userId || selectedRoleIds.length === 0) return;
    try {
      setAssignLoading(true);

      // 获取选中角色的名称
      const selectedRoleNames = selectedRoleIds
        .map((id) => {
          const role = allRoles.find((role) => role.id === id);
          return role?.name;
        })
        .filter(Boolean) as string[];

      // 使用批量分配方法
      const results = await adminApi.roles.assignMultipleRoles(
        userId,
        selectedRoleNames
      );

      // 检查结果
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (failCount === 0) {
        message.success(`成功分配 ${successCount} 个角色`);
      } else if (successCount === 0) {
        message.error(`分配角色失败`);
      } else {
        message.warning(`成功分配 ${successCount} 个角色，${failCount} 个失败`);
      }

      setIsAssignModalVisible(false);
      setSelectedRoleIds([]);
      fetchUserRoles();
    } catch (error: any) {
      message.error(error.message || "分配角色失败");
    } finally {
      setAssignLoading(false);
    }
  };

  const columns = [
    {
      title: "角色名称",
      dataIndex: "display_name",
      width: 100,
    },
    {
      title: "角色标识",
      dataIndex: "name",
      width: 100,
    },
    {
      title: "描述",
      dataIndex: "description",
      width: 120,
      ellipsis: true,
    },
    {
      title: "操作",
      dataIndex: "actions",
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: Role) => {
        return (
          <BasePopconfirm
            title="确定要撤销这个角色吗？"
            onConfirm={() => handleRevokeRole(record)}
            okText="确定"
            cancelText="取消"
          >
            <BaseButton
              type="default"
              size="small"
              danger
              icon={<MinusOutlined />}
            >
              撤销
            </BaseButton>
          </BasePopconfirm>
        );
      },
    },
  ];

  // 获取可分配的角色（排除已有角色）
  const availableRoles = allRoles.filter(
    (role) => !userRoles.some((userRole) => userRole.id === role.id)
  );

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Title level={5}>{employeeName}的角色管理</Typography.Title>
        <BaseButton
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAssignModalVisible(true)}
          disabled={availableRoles.length === 0}
          style={{ marginLeft: "auto", marginRight: 20 }}
        >
          分配角色
        </BaseButton>
        <BaseButton
          type="primary"
          icon={<RedoOutlined />}
          loading={loading}
          onClick={fetchUserRoles}
        >
          刷新
        </BaseButton>
      </div>

      <BaseTable
        bordered
        dataSource={userRoles}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={false}
        // scroll={{ x: 100 }}
        locale={{
          emptyText: "暂无角色",
        }}
      />

      {/* 分配角色模态框 */}
      <Modal
        title="分配角色"
        visible={isAssignModalVisible}
        onOk={handleAssignRoles}
        onCancel={() => {
          setIsAssignModalVisible(false);
          setSelectedRoleIds([]);
        }}
        confirmLoading={assignLoading}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Typography.Text>
            为 {employeeName} 选择要分配的角色：
          </Typography.Text>
        </div>
        <div style={{ maxHeight: 400, overflowY: "auto" }}>
          {availableRoles.map((role) => (
            <div
              key={role.id}
              style={{
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                marginBottom: "8px",
                cursor: "pointer",
                backgroundColor: selectedRoleIds.includes(role.id)
                  ? "#e6f7ff"
                  : "#fff",
                borderColor: selectedRoleIds.includes(role.id)
                  ? "#1890ff"
                  : "#d9d9d9",
              }}
              onClick={() => {
                if (selectedRoleIds.includes(role.id)) {
                  setSelectedRoleIds(
                    selectedRoleIds.filter((id) => id !== role.id)
                  );
                } else {
                  setSelectedRoleIds([...selectedRoleIds, role.id]);
                }
              }}
            >
              <div style={{ fontWeight: "bold" }}>{role.display_name}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {role.name} - {role.description}
              </div>
            </div>
          ))}
        </div>
        {availableRoles.length === 0 && (
          <div style={{ textAlign: "center", color: "#999", padding: "20px" }}>
            没有可分配的角色
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RoleManagement;
